import React, { useState, useEffect, useRef } from 'react';
import { getDesignerTemplates } from '../../services/workflowService';
import type { ConnectorProperty } from '../../services/workflowService';
import { getForms } from '../../services/formService';
import type { Form } from '../../services/formService';
import { Search, X, Check, Code, Play, Trash2, HelpCircle } from 'lucide-react';

interface CustomPropertiesPanelProps {
    modeler: any;
    selectedElement: any | null;
}

interface LocalValues {
    [key: string]: string;
}

const CustomPropertiesPanel: React.FC<CustomPropertiesPanelProps> = ({ modeler, selectedElement }) => {
    const [customProperties, setCustomProperties] = useState<ConnectorProperty[]>([]);
    const [localValues, setLocalValues] = useState<LocalValues>({});

    // Connector schemas keyed by their implementation (camunda:class / delegateExpression).
    // We look properties up here instead of trusting `custom:properties` — that attribute
    // lives in an unregistered XML namespace and is silently stripped on save / reload,
    // which is what made the connector tab vanish after deploy.
    const [propertiesByImplementation, setPropertiesByImplementation] = useState<
        Record<string, ConnectorProperty[]>
    >({});

    // Same idea but keyed by the template's `topic` (now persisted on the
    // element as `camunda:modelerTemplate`). Required for connector-style
    // templates (e.g. `conn-document-ai`) whose `decodedContent.implementation`
    // is empty — they have no Java FQCN to index by, so the
    // by-implementation lookup above misses them and the panel renders
    // blank after a save+reload.
    const [propertiesByTopic, setPropertiesByTopic] = useState<
        Record<string, ConnectorProperty[]>
    >({});

    // Form selection state for UserTask/StartEvent
    const [forms, setForms] = useState<Form[]>([]);
    const [formsLoading, setFormsLoading] = useState(false);
    const [selectedFormKey, setSelectedFormKey] = useState<string>('');
    const [formSearchQuery, setFormSearchQuery] = useState('');
    const [isFormDropdownOpen, setIsFormDropdownOpen] = useState(false);
    const formDropdownRef = useRef<HTMLDivElement>(null);

    // Script Task state
    const [scriptContent, setScriptContent] = useState<string>('');
    const [scriptFormat, setScriptFormat] = useState<string>('javascript');

    // Check if element is UserTask or StartEvent
    const isUserTaskOrStartEvent = (element: any): boolean => {
        if (!element?.businessObject?.$type) return false;
        const type = element.businessObject.$type;
        return type === 'bpmn:UserTask' || type === 'bpmn:StartEvent';
    };

    // Check if element is Script Task
    const isScriptTaskElement = (element: any): boolean => {
        if (!element?.businessObject?.$type) return false;
        return element.businessObject.$type === 'bpmn:ScriptTask';
    };

    const isUserTask = isUserTaskOrStartEvent(selectedElement);
    const isScriptTask = isScriptTaskElement(selectedElement);

    // Fetch forms for UserTask/StartEvent with debounced search
    useEffect(() => {
        const fetchFormsData = async () => {
            if (!isUserTask) return;

            setFormsLoading(true);
            try {
                // Perform backend search
                const response = await getForms(0, 200, formSearchQuery || undefined, "createdDate,DESC");
                setForms(response.content || []);
            } catch (error) {
                console.error('Error fetching forms:', error);
            } finally {
                setFormsLoading(false);
            }
        };

        const timer = setTimeout(() => {
            fetchFormsData();
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [isUserTask, formSearchQuery]);

    // Get current form value from element
    useEffect(() => {
        if (selectedElement?.businessObject && isUserTask) {
            const formKey = selectedElement.businessObject.get('camunda:formKey') || '';
            setSelectedFormKey(formKey);
        } else {
            setSelectedFormKey('');
        }
    }, [selectedElement, isUserTask]);

    // Get current script value from element
    useEffect(() => {
        if (selectedElement?.businessObject && isScriptTask) {
            const script = selectedElement.businessObject.get('script') || '';
            const format = selectedElement.businessObject.get('scriptFormat') || 'javascript';
            setScriptContent(script);
            setScriptFormat(format);
        } else {
            setScriptContent('');
            setScriptFormat('javascript');
        }
    }, [selectedElement, isScriptTask]);

    // Handle form selection change
    const handleFormChange = (value: string) => {
        if (!modeler || !selectedElement) return;

        const modeling = modeler.get('modeling');

        // Update only camunda:formKey (standard Camunda extension)
        modeling.updateProperties(selectedElement, {
            'camunda:formKey': value
        });

        setSelectedFormKey(value);
    };

    // Handle script content change
    const handleScriptChange = (script: string) => {
        setScriptContent(script);
    };

    // Save script content on blur
    const handleScriptBlur = () => {
        if (!modeler || !selectedElement || !isScriptTask) return;

        const modeling = modeler.get('modeling');
        modeling.updateProperties(selectedElement, {
            script: scriptContent,
            scriptFormat: scriptFormat
        });
    };

    // Handle script format change
    const handleScriptFormatChange = (format: string) => {
        if (!modeler || !selectedElement || !isScriptTask) return;

        setScriptFormat(format);
        const modeling = modeler.get('modeling');
        modeling.updateProperties(selectedElement, {
            scriptFormat: format
        });
    };

    /**
     * Remove the currently-selected element from the BPMN canvas.
     *
     * Uses bpmn-js's modeling service so deletion goes through the command
     * stack — Ctrl+Z still restores the element. We block deletion of the
     * root Process / Collaboration so users can't accidentally nuke the
     * whole diagram from the panel (those elements would silently fail or
     * leave the modeler in a bad state).
     *
     * A `window.confirm` prompt is intentional: deletion from the panel is
     * a deliberate action, not part of normal drag/drop, and the prompt
     * surfaces it as such. Ctrl+Z still works as a safety net.
     */
    const handleDeleteElement = () => {
        if (!modeler || !selectedElement) return;

        const businessType = (selectedElement as any).businessObject?.$type || '';
        if (
            businessType === 'bpmn:Process' ||
            businessType === 'bpmn:Collaboration' ||
            businessType === 'bpmn:Definitions'
        ) {
            // The root cannot be removed; the modeler would throw or end
            // up in an inconsistent state. The button should already be
            // hidden for these, this is just belt-and-braces.
            return;
        }

        const label =
            (selectedElement as any).businessObject?.name ||
            (selectedElement as any).id ||
            'this element';
        if (!window.confirm(`Delete "${label}"? Use Ctrl+Z to restore.`)) {
            return;
        }

        try {
            const modeling = modeler.get('modeling');
            modeling.removeElements([selectedElement]);
        } catch (err) {
            // bpmn-js throws if the element can't be removed (e.g. a
            // diagram-only label without a corresponding shape). Surface
            // it in the console rather than failing silently.
            console.error('[CustomPropertiesPanel] Failed to delete element', err);
        }
    };

    // Load designer templates once on mount and index them by every identifier
    // a saved BPMN task might carry on `camunda:modelerTemplate`:
    //   • implementation FQCN — for Java-delegate templates (OCR Upload, etc.)
    //   • topic               — the canonical template id
    //   • type                — same id under a different field name for
    //                           templates authored before topic was the
    //                           standard (e.g. some `conn-*` connector
    //                           templates only have `decodedContent.type`)
    //   • name                — last-resort fallback so a hand-edited XML
    //                           that put the human name into
    //                           `camunda:modelerTemplate` still resolves
    // On element selection we look the schema up here, so the connector tab
    // survives a BPMN save/reload round-trip regardless of which field the
    // template definition uses for its id.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const templates = await getDesignerTemplates();
                if (cancelled) return;
                const byImpl: Record<string, ConnectorProperty[]> = {};
                const byTopic: Record<string, ConnectorProperty[]> = {};
                for (const t of templates) {
                    const dc: any = t.decodedContent || {};
                    const impl = dc.implementation;
                    const props = dc.properties;
                    if (!Array.isArray(props)) continue;
                    if (impl) {
                        byImpl[impl] = props;
                    }
                    // Index under every alternative id the template carries.
                    // The `propertiesByTopic` name is kept for compatibility
                    // even though the lookup is no longer topic-only.
                    const altKeys: string[] = [];
                    if (typeof dc.topic === 'string' && dc.topic) altKeys.push(dc.topic);
                    if (typeof dc.type === 'string' && dc.type) altKeys.push(dc.type);
                    if (typeof t.name === 'string' && t.name) altKeys.push(t.name);
                    for (const key of altKeys) {
                        // Only set if not already taken so an earlier match
                        // (e.g. topic) wins over a later one (e.g. name).
                        if (!byTopic[key]) byTopic[key] = props;
                    }
                }
                // eslint-disable-next-line no-console
                console.log('[CustomPropertiesPanel] template index built', {
                    byImplKeys: Object.keys(byImpl),
                    byTopicKeys: Object.keys(byTopic),
                });
                setPropertiesByImplementation(byImpl);
                setPropertiesByTopic(byTopic);
            } catch (e) {
                console.warn('CustomPropertiesPanel: failed to load designer templates', e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Update properties when selected element or templates registry changes.
    useEffect(() => {
        if (selectedElement && selectedElement.businessObject) {
            const businessObject = selectedElement.businessObject;

            // 1. Preferred path — look up the schema by class / delegateExpression /
            //    expression / topic against the templates registry. This survives BPMN
            //    serialization. Try every accessor pattern bpmn-js / camunda moddle uses,
            //    because the same XML attribute can surface as `camunda:class`, `class`,
            //    or sit on `$attrs` depending on whether the moddle entry resolved.
            const readAttr = (key: string): string => {
                try {
                    const v = (businessObject as any).get?.(key);
                    if (v) return String(v);
                } catch {
                    /* ignore */
                }
                const stripped = key.includes(':') ? key.split(':')[1] : key;
                if ((businessObject as any)[stripped]) return String((businessObject as any)[stripped]);
                const attrs = (businessObject as any).$attrs || {};
                return String(attrs[key] || attrs[stripped] || '');
            };

            const impl =
                readAttr('camunda:class') ||
                readAttr('camunda:delegateExpression') ||
                readAttr('camunda:expression') ||
                '';

            const fromTemplate =
                (impl && propertiesByImplementation[impl]) ||
                undefined;

            // 2. Connector templates have no `camunda:class` to match on —
            //    they're tagged with `camunda:modelerTemplate="<topic>"`.
            //    Look the schema up by topic so the Document AI tab (and
            //    any other connector template) renders correctly after a
            //    save/reload, not only when the template is first applied.
            const templateTopic =
                readAttr('camunda:modelerTemplate') ||
                // Very old diagrams used `camunda:customElement` for the
                // same purpose — still tolerated for back-compat.
                readAttr('camunda:customElement') ||
                '';

            // Diagnostic — temporary. Tells us exactly what the panel
            // reads off the selected element and whether the
            // implementation/topic indexes have a matching entry.
            // Remove once the connector tab renders reliably.
            // eslint-disable-next-line no-console
            console.log('[CustomPropertiesPanel] selection', {
                elementId: (selectedElement as any).id,
                $type: businessObject.$type,
                impl,
                templateTopic,
                implMatch: !!(impl && propertiesByImplementation[impl]),
                topicMatch: !!(templateTopic && propertiesByTopic[templateTopic]),
                knownImplKeys: Object.keys(propertiesByImplementation),
                knownTopicKeys: Object.keys(propertiesByTopic),
            });

            if (fromTemplate && fromTemplate.length > 0) {
                // NOTE: we no longer filter out hidden properties here —
                // they may carry `template` / `combineInto` metadata that
                // the panel needs to drive value propagation (e.g. a
                // hidden `payload` that interpolates other inputs).
                // Hidden properties are skipped at render time instead.
                setCustomProperties(fromTemplate);
                return;
            }

            const fromTopic =
                (templateTopic && propertiesByTopic[templateTopic]) ||
                undefined;

            if (fromTopic && fromTopic.length > 0) {
                setCustomProperties(fromTopic);
                return;
            }

            // 3. Fallback — legacy in-memory custom:properties attribute. Works for
            //    elements that were just dropped on the canvas in the current session
            //    but is dropped on serialization (custom: namespace isn't registered).
            const customPropsStr = businessObject.get('custom:properties');
            if (customPropsStr) {
                try {
                    const custProps = JSON.parse(customPropsStr);
                    const properties = Array.isArray(custProps) ? custProps : [];
                    setCustomProperties(properties);
                    return;
                } catch {
                    /* fall through */
                }
            }
            setCustomProperties([]);
        } else {
            setCustomProperties([]);
        }
    }, [selectedElement, propertiesByImplementation, propertiesByTopic]);

    // Handle click outside to close form dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (formDropdownRef.current && !formDropdownRef.current.contains(event.target as Node)) {
                setIsFormDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    // Re-initialise localValues from the BPMN model only when the SELECTED ELEMENT
    // ID changes. Keying on the full `selectedElement` reference (or on
    // `customProperties`) caused the panel to clobber the user's in-progress edits
    // every time React produced a new reference for the same element — which is
    // why values in the connector properties tab "didn't update until refresh".
    useEffect(() => {
        if (!selectedElement?.businessObject) {
            setLocalValues({});
            return;
        }

        const businessObject = selectedElement.businessObject;
        const newLocalValues: LocalValues = {};

        // Route reads through findActiveInputOutput so connector tasks
        // populate from <camunda:connector>/<camunda:inputOutput> rather
        // than the outer InputOutput (which may be empty or carry stale
        // values from before the connector wrapper was introduced).
        const inputOutput = findActiveInputOutput(businessObject);
        if (inputOutput) {
            inputOutput.inputParameters?.forEach((param: any) => {
                newLocalValues[param.name] = param.value || '';
            });
            inputOutput.outputParameters?.forEach((param: any) => {
                newLocalValues[param.name] = param.value || '';
            });
        }

        setLocalValues(newLocalValues);
        // Intentionally narrow dependency to the element id. We do NOT want this
        // to re-run when the customProperties array gets a new reference (e.g.
        // after the templates registry resolves), because that wipes typed-but-
        // not-yet-blurred values.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedElement?.id]);

    // Helper function to get or create extension elements
    const getOrCreateExtensionElements = (businessObject: any, moddle: any) => {
        if (!businessObject.extensionElements) {
            businessObject.extensionElements = moddle.create('bpmn:ExtensionElements');
            businessObject.extensionElements.values = [];
        }
        return businessObject.extensionElements;
    };

    /**
     * Locate the InputOutput container appropriate for this element.
     *
     * For a Camunda Connect task the parameters MUST live inside
     *   <camunda:connector>/<camunda:inputOutput>
     * so the connector framework reads them. For a plain Java-delegate
     * task they live in the direct child
     *   <bpmn:extensionElements>/<camunda:inputOutput>
     *
     * We detect "is this a connector task?" by the presence of a
     * <camunda:Connector> child on extensionElements — by the time the
     * user reaches this panel, SimpleBpmnEditor's heal-legacy pass has
     * already wrapped any `conn-*` modelerTemplate task in a Connector,
     * so this check is reliable.
     *
     * Returns null when no container exists yet. Use the
     * `getOrCreate` variant below for write paths.
     */
    const findActiveInputOutput = (businessObject: any): any | null => {
        const ext = businessObject?.extensionElements;
        const values: any[] | undefined = ext?.values;
        if (!values) return null;
        const connector = values.find((el: any) => el.$type === 'camunda:Connector');
        if (connector) {
            return connector.inputOutput || null;
        }
        return values.find((el: any) => el.$type === 'camunda:InputOutput') || null;
    };

    /**
     * Same as findActiveInputOutput but creates the container if it
     * doesn't exist yet. For connector tasks the inputOutput is nested
     * inside the existing Connector element; for non-connector tasks
     * it's a direct child of extensionElements.
     */
    const getOrCreateActiveInputOutput = (extensionElements: any, moddle: any): any => {
        const values: any[] = extensionElements.values || [];
        const connector = values.find((el: any) => el.$type === 'camunda:Connector');

        if (connector) {
            if (!connector.inputOutput) {
                connector.inputOutput = moddle.create('camunda:InputOutput');
            }
            connector.inputOutput.inputParameters = connector.inputOutput.inputParameters || [];
            connector.inputOutput.outputParameters = connector.inputOutput.outputParameters || [];
            return connector.inputOutput;
        }

        let inputOutput = values.find((el: any) => el.$type === 'camunda:InputOutput');
        if (!inputOutput) {
            inputOutput = moddle.create('camunda:InputOutput');
            extensionElements.values.push(inputOutput);
        }
        inputOutput.inputParameters = inputOutput.inputParameters || [];
        inputOutput.outputParameters = inputOutput.outputParameters || [];
        return inputOutput;
    };

    /**
     * Substitute `{{propertyId}}` placeholders in a template string with
     * the current value of each referenced sibling property. Lookup
     * priority for a given placeholder:
     *   1. nextValues map (the panel's localValues, optionally with an
     *      in-flight edit merged in by the caller)
     *   2. the property's `default`
     *   3. empty string
     *
     * Only basic `{{id}}` placeholders are supported (alphanumeric +
     * underscore + dot) — no helpers, no escaping. The output of the
     * template is written verbatim as the target's parameter value,
     * which for connector inputs usually IS a JSON string.
     */
    const interpolateTemplate = (
        template: string,
        properties: ConnectorProperty[],
        nextValues: LocalValues,
    ): string => {
        return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, varName: string) => {
            if (varName in nextValues && nextValues[varName] != null) {
                return String(nextValues[varName]);
            }
            const sourceProp = properties.find(p => p.id === varName);
            return sourceProp?.default ?? '';
        });
    };

    /**
     * Write a single input/output parameter into the live InputOutput
     * container, updating the existing entry if present, creating a new
     * one if not. Pure mutation — the caller is responsible for
     * notifying the modeler via `modeling.updateProperties`.
     */
    const writeParam = (
        inputOutput: any,
        prop: ConnectorProperty,
        value: string,
        moddle: any,
    ): void => {
        const parameterType = prop.parameterType === 'output' ? 'output' : 'input';
        const list = parameterType === 'output'
            ? inputOutput.outputParameters
            : inputOutput.inputParameters;
        const moddleType = parameterType === 'output'
            ? 'camunda:OutputParameter'
            : 'camunda:InputParameter';

        const existing = list.find((p: any) => p.name === prop.id);
        if (existing) {
            existing.value = value;
        } else {
            list.push(moddle.create(moddleType, { name: prop.id, value }));
        }
    };

    const updateProperty = (prop: ConnectorProperty, value: string) => {
        if (!modeler || !selectedElement) return;

        const modeling = modeler.get('modeling');
        const moddle = modeler.get('moddle');
        const businessObject = selectedElement.businessObject;

        // Get or create extension elements + the appropriate inputOutput.
        // For connector tasks this is connector.inputOutput; for plain
        // Java-delegate tasks it's the outer InputOutput.
        const extensionElements = getOrCreateExtensionElements(businessObject, moddle);
        const inputOutput = getOrCreateActiveInputOutput(extensionElements, moddle);

        // 1. Write the edited property's value.
        writeParam(inputOutput, prop, value, moddle);

        // 2. If this property feeds into a composite target (via
        //    `combineInto.targetProperty`), recompute the target's value
        //    by interpolating its template, then write that too.
        //
        //    Example: editing `sourceDocument` triggers a recompute of
        //    `payload` whose template is
        //    `{ "document_base64": "{{sourceDocument}}", ... }`. We
        //    merge the in-flight value into the lookup map so the new
        //    `sourceDocument` is reflected before localValues has had a
        //    chance to settle.
        const nextValues: LocalValues = { ...localValues, [prop.id]: value };
        const targetId = prop.combineInto?.targetProperty;
        if (targetId) {
            const target = customProperties.find(p => p.id === targetId);
            if (target?.template) {
                const interpolated = interpolateTemplate(
                    target.template,
                    customProperties,
                    nextValues,
                );
                writeParam(inputOutput, target, interpolated, moddle);
                nextValues[target.id] = interpolated;
            }
        }

        // 3. Push the merged map back into local state so the panel UI
        //    reflects any auto-computed target values immediately.
        setLocalValues(nextValues);

        // 4. Single modeler notification covers all mutations above.
        modeling.updateProperties(selectedElement, {
            extensionElements: extensionElements,
        });
    };

    const handleInputChange = (key: string, value: string) => {
        setLocalValues(prev => ({ ...prev, [key]: value }));
    };

    const handleInputBlur = (prop: ConnectorProperty) => {
        if (localValues[prop.id] !== undefined) {
            updateProperty(prop, localValues[prop.id]);
        }
    };

    /**
     * Remove an input/output parameter from the appropriate InputOutput
     * container. For connector tasks that's <camunda:connector>/<camunda:inputOutput>;
     * for non-connector tasks it's the outer <camunda:inputOutput>. Used
     * by the trash icon next to each Input/Output Parameter row.
     */
    const deleteProperty = (prop: ConnectorProperty) => {
        if (!modeler || !selectedElement) return;

        const modeling = modeler.get('modeling');
        const businessObject = selectedElement.businessObject;
        const extensionElements = businessObject.extensionElements;
        if (!extensionElements?.values) return;

        const inputOutput = findActiveInputOutput(businessObject);
        if (!inputOutput) return;

        if (prop.parameterType === 'input') {
            inputOutput.inputParameters = (inputOutput.inputParameters || []).filter(
                (p: any) => p.name !== prop.id
            );
        } else if (prop.parameterType === 'output') {
            inputOutput.outputParameters = (inputOutput.outputParameters || []).filter(
                (p: any) => p.name !== prop.id
            );
        }

        modeling.updateProperties(selectedElement, {
            extensionElements: extensionElements,
        });

        // Drop the value from local state so the field renders empty after delete.
        setLocalValues(prev => {
            const next = { ...prev };
            delete next[prop.id];
            return next;
        });
    };

    /**
     * Evaluate a property's {@code showWhen} clause against the current field
     * values. Returns false when the dependent field's current value isn't in
     * the allowed list — used to hide e.g. OAuth fields when authMode=SERVICE_ACCOUNT.
     */
    const isPropertyVisible = (prop: ConnectorProperty): boolean => {
        if (!prop.showWhen || !prop.showWhen.field) return true;
        const allowed = prop.showWhen.value || [];
        if (allowed.length === 0) return true;
        const current = localValues[prop.showWhen.field] ?? '';
        return allowed.includes(current);
    };

    /**
     * Hover-tooltip rendering of the property description. The icon sits inline
     * with the label; the tooltip appears on hover/focus via the Tailwind `group`
     * utility (no extra dependency). Returns null when the prop has no
     * description so labels stay visually aligned.
     */
    const renderHelpIcon = (prop: ConnectorProperty) => {
        if (!prop.description) return null;
        return (
            <span className="relative inline-flex group">
                <HelpCircle
                    size={13}
                    className="text-gray-400 hover:text-gray-600 cursor-help"
                    aria-hidden="true"
                />
                <span className="sr-only">Help: {prop.description}</span>
                {/* Tooltip — hidden by default, fades in on hover/focus-within */}
                <span
                    role="tooltip"
                    className="
                        pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5
                        z-50 hidden group-hover:block group-focus-within:block
                        w-64 max-w-[16rem] p-2.5
                        rounded-md bg-gray-900 text-white text-xs leading-snug
                        shadow-lg whitespace-pre-line
                    "
                >
                    {prop.description}
                    {/* arrow */}
                    <span className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0
                        border-l-[5px] border-l-transparent
                        border-r-[5px] border-r-transparent
                        border-t-[5px] border-t-gray-900" />
                </span>
            </span>
        );
    };

    /**
     * Header row: label (with optional ? icon for hover help), required asterisk,
     * and (for input/output rows) a small trash icon that removes the underlying
     * camunda:InputParameter / camunda:OutputParameter from the selected
     * element's extensionElements.
     */
    const renderFieldHeader = (prop: ConnectorProperty) => {
        const isDeletable = prop.parameterType === 'input' || prop.parameterType === 'output';
        return (
            <div className="flex items-start justify-between gap-2 mb-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                    <span>{prop.label}</span>
                    {prop.required && <span className="text-red-500">*</span>}
                    {renderHelpIcon(prop)}
                </label>
                {isDeletable && (
                    <button
                        type="button"
                        onClick={() => deleteProperty(prop)}
                        title={`Remove ${prop.label}`}
                        aria-label={`Remove ${prop.label}`}
                        className="shrink-0 p-1 -mt-1 -mr-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                        <Trash2 size={14} />
                    </button>
                )}
            </div>
        );
    };

    const renderPropertyField = (prop: ConnectorProperty) => {
        const baseInputClass = "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all";

        switch (prop.type) {
            case 'select':
                return (
                    <div key={prop.id} className="mb-4">
                        {renderFieldHeader(prop)}
                        <select
                            value={localValues[prop.id] || ''}
                            onChange={e => handleInputChange(prop.id, e.target.value)}
                            onBlur={() => handleInputBlur(prop)}
                            className={baseInputClass}
                        >
                            <option value="">Select...</option>
                            {prop.options?.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                );

            case 'json':
            case 'mapped-json':
                return (
                    <div key={prop.id} className="mb-4">
                        {renderFieldHeader(prop)}
                        <textarea
                            value={localValues[prop.id] || ''}
                            onChange={e => handleInputChange(prop.id, e.target.value)}
                            onBlur={() => handleInputBlur(prop)}
                            placeholder={prop.placeholder || prop.label}
                            className={`${baseInputClass} min-h-[80px] font-mono text-xs`}
                        />
                    </div>
                );

            default:
                return (
                    <div key={prop.id} className="mb-4">
                        {renderFieldHeader(prop)}
                        <input
                            type="text"
                            value={localValues[prop.id] || ''}
                            onChange={e => handleInputChange(prop.id, e.target.value)}
                            onBlur={() => handleInputBlur(prop)}
                            placeholder={prop.placeholder || prop.label}
                            className={baseInputClass}
                        />
                    </div>
                );
        }
    };

    // Render Form Selector for UserTask/StartEvent
    const renderFormSelector = () => {
        if (!isUserTask) return null;

        // Get selected form name for display
        const selectedForm = forms.find(f => `api/form-contents/name/${f.nameSlug}` === selectedFormKey);

        const getDisplayName = () => {
            if (isFormDropdownOpen) return formSearchQuery;
            if (selectedForm) return selectedForm.name;
            if (selectedFormKey) {
                const match = selectedFormKey.match(/name\/(.+)$/);
                return match ? match[1] : selectedFormKey;
            }
            return '';
        };

        // Forms are already filtered by backend
        const filteredForms = forms;

        const handleSelectForm = (form: Form) => {
            handleFormChange(`api/form-contents/name/${form.nameSlug}`);
            setIsFormDropdownOpen(false);
            setFormSearchQuery('');
        };

        const handleClearForm = () => {
            handleFormChange('');
            setFormSearchQuery('');
        };

        return (
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded bg-indigo-100 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                        </svg>
                    </div>
                    <h4 className="text-sm font-semibold text-gray-900">DoIT Forms</h4>
                </div>
                <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                        Choose a DoIT form
                    </label>

                    {/* Searchable Dropdown */}
                    <div ref={formDropdownRef} className="relative">
                        {/* Selected value or search input */}
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search size={14} className="text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={getDisplayName()}
                                onChange={(e) => {
                                    setFormSearchQuery(e.target.value);
                                    if (!isFormDropdownOpen) setIsFormDropdownOpen(true);
                                }}
                                onFocus={() => setIsFormDropdownOpen(true)}
                                placeholder={formsLoading ? "Loading forms..." : "Search forms..."}
                                disabled={formsLoading}
                                className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                            />
                            {selectedFormKey && !isFormDropdownOpen && (
                                <button
                                    onClick={handleClearForm}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        {/* Dropdown list */}
                        {isFormDropdownOpen && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                {/* None option */}
                                <button
                                    onClick={() => {
                                        handleFormChange('');
                                        setIsFormDropdownOpen(false);
                                        setFormSearchQuery('');
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 text-left"
                                >
                                    <span className="w-4 h-4 flex items-center justify-center">
                                        {!selectedFormKey && <Check size={14} className="text-indigo-600" />}
                                    </span>
                                    &lt;none&gt;
                                </button>

                                {filteredForms.length === 0 ? (
                                    <div className="px-3 py-4 text-sm text-gray-500 text-center">
                                        No forms found
                                    </div>
                                ) : (
                                    filteredForms.map(form => {
                                        const formValue = `api/form-contents/name/${form.nameSlug}`;
                                        const isSelected = formValue === selectedFormKey;
                                        return (
                                            <button
                                                key={form.id}
                                                onClick={() => handleSelectForm(form)}
                                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${isSelected ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                                                    }`}
                                            >
                                                <span className="w-4 h-4 flex items-center justify-center">
                                                    {isSelected && <Check size={14} className="text-indigo-600" />}
                                                </span>
                                                <span className="truncate">{form.name}</span>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>

                    {selectedFormKey && (
                        <p className="text-xs text-gray-500 mt-1 truncate" title={selectedFormKey}>
                            Form Key: {selectedFormKey}
                        </p>
                    )}
                </div>
            </div>
        );
    };

    // Render Script Editor for ScriptTask
    const renderScriptEditor = () => {
        if (!isScriptTask) return null;

        const scriptFormats = [
            { value: 'javascript', label: 'JavaScript' },
            { value: 'groovy', label: 'Groovy' },
            { value: 'python', label: 'Python' },
            { value: 'ruby', label: 'Ruby' },
            { value: 'juel', label: 'JUEL' }
        ];

        return (
            <div className="flex flex-col h-full">
                <div className="flex items-center gap-2 mb-3 px-4 pt-4">
                    <div className="w-6 h-6 rounded bg-amber-100 flex items-center justify-center">
                        <Code size={14} className="text-amber-600" />
                    </div>
                    <h4 className="text-sm font-semibold text-gray-900">Script Editor</h4>
                </div>

                <div className="px-4 mb-3">
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                        Script Format
                    </label>
                    <select
                        value={scriptFormat}
                        onChange={(e) => handleScriptFormatChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all"
                    >
                        {scriptFormats.map(format => (
                            <option key={format.value} value={format.value}>
                                {format.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="px-4 flex-1 flex flex-col min-h-0">
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                        Script Content
                    </label>
                    <textarea
                        value={scriptContent}
                        onChange={(e) => handleScriptChange(e.target.value)}
                        onBlur={handleScriptBlur}
                        placeholder="// Enter your script here..."
                        className="flex-1 min-h-[200px] w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all font-mono bg-gray-50 resize-none"
                        spellCheck={false}
                    />
                    <div className="flex items-center justify-between mt-2 mb-4">
                        <p className="text-xs text-gray-500">
                            {scriptContent.split('\n').length} lines
                        </p>
                        <button
                            onClick={handleScriptBlur}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded hover:bg-amber-100 transition-colors"
                        >
                            <Play size={12} />
                            Save Script
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Decide whether to render the panel. We always render it for a
     * concrete element selection so the element-level Delete button is
     * reachable (previously the panel hid itself for "vanilla" shapes
     * that had no template / form / script, leaving Delete unreachable
     * from the side panel and forcing users to right-click the canvas).
     *
     * The exception is the diagram root (Process / Collaboration /
     * Definitions): selecting empty canvas isn't a useful editing
     * context, so we still hide the panel in that case.
     */
    if (!selectedElement) return null;
    const businessType = (selectedElement as any).businessObject?.$type || '';
    const isRoot =
        businessType === 'bpmn:Process' ||
        businessType === 'bpmn:Collaboration' ||
        businessType === 'bpmn:Definitions';
    if (isRoot) return null;

    // Humanise the bpmn:* type for the header. "bpmn:ServiceTask" → "Service Task".
    const elementTypeLabel = businessType
        .replace(/^bpmn:/, '')
        .replace(/([a-z])([A-Z])/g, '$1 $2');
    const elementId = (selectedElement as any).id || '';
    const elementName = (selectedElement as any).businessObject?.name || '';

    // Group properties by parameter type. We render only the non-hidden
    // ones that pass their showWhen guard — hidden properties stay in
    // `customProperties` for template/combineInto propagation but never
    // appear in the UI.
    const visibleProperties = customProperties.filter(p => !p.hidden && isPropertyVisible(p));
    const inputProperties = visibleProperties.filter(p => p.parameterType === 'input');
    const outputProperties = visibleProperties.filter(p => p.parameterType === 'output');
    const otherProperties = visibleProperties.filter(p => !p.parameterType || p.parameterType === 'property');
    // Has anything to render in the body? Check visibleProperties, not
    // customProperties — a template with only hidden props (rare) would
    // otherwise show an empty body.
    const hasPanelBody =
        visibleProperties.length > 0 || isUserTask || isScriptTask;

    return (
        <div className="flex-1 overflow-y-auto min-w-[320px]">
            {/* Element header — always shown for a non-root selection.
                Sticky so the Delete button stays reachable as the user
                scrolls through long property lists. */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                            {elementTypeLabel || 'Element'}
                        </div>
                        <div
                            className="text-sm font-medium text-gray-900 truncate"
                            title={elementName ? `${elementName} (${elementId})` : elementId}
                        >
                            {elementName || elementId || 'Untitled'}
                        </div>
                        {elementName && elementId && (
                            <div className="text-[11px] text-gray-400 font-mono truncate">
                                {elementId}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleDeleteElement}
                        className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 rounded-md transition-colors"
                        title="Delete this element"
                        aria-label={`Delete ${elementTypeLabel} ${elementId}`}
                    >
                        <Trash2 size={14} />
                        Delete
                    </button>
                </div>
            </div>

            {/* Script Editor for ScriptTask */}
            {isScriptTask && renderScriptEditor()}

            {/* Properties Content - scrollable (for UserTask and connectors) */}
            {!isScriptTask && hasPanelBody && (
                <div className="p-4">
                    {/* DoIT Forms Selector for UserTask/StartEvent */}
                    {renderFormSelector()}

                    {/* Divider if both form selector and custom properties exist */}
                    {isUserTask && customProperties.length > 0 && (
                        <div className="border-t border-gray-200 my-4" />
                    )}

                    {/* Property Parameters Header */}
                    {customProperties.length > 0 && (
                        <h4 className="text-sm font-medium text-gray-900 mb-4">Property Parameters</h4>
                    )}

                    {/* Input Parameters */}
                    {inputProperties.length > 0 && (
                        <div className="mb-6">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                Input Parameters
                            </h4>
                            {inputProperties.map(prop => renderPropertyField(prop))}
                        </div>
                    )}

                    {/* Output Parameters */}
                    {outputProperties.length > 0 && (
                        <div className="mb-6">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                Output Parameters
                            </h4>
                            {outputProperties.map(prop => renderPropertyField(prop))}
                        </div>
                    )}

                    {/* Other Properties */}
                    {otherProperties.length > 0 && (
                        <div className="mb-6">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                General Properties
                            </h4>
                            {otherProperties.map(prop => renderPropertyField(prop))}
                        </div>
                    )}
                </div>
            )}

            {/* Empty-state hint when the element has no configurable
                properties — keeps the panel useful (Delete is still in
                the header) without an unexplained blank space. */}
            {!isScriptTask && !hasPanelBody && (
                <div className="p-4">
                    <p className="text-xs text-gray-400 italic">
                        This element has no configurable properties. Use the
                        Delete button above to remove it, or press the Delete
                        key with it selected on the canvas.
                    </p>
                </div>
            )}
        </div>
    );
};

export default CustomPropertiesPanel;
